import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  BookOpen,
  Trophy,
  Target,
  Clock,
  CheckCircle2,
  ArrowRight,
  BarChart3,
  GraduationCap,
  Flame,
  Award
} from "lucide-react";
import { toast } from "sonner";

interface CourseStats {
  courseId: string;
  courseTitle: string;
  courseDescription: string;
  enrolledAt: string;
  objectives: string[];
  /** AI Coach: objective mastery from learner_objective_mastery (preferred over objectives when present) */
  objectiveMastery?: Array<{ objectiveId: string; title: string; masteryScore: number }>;
  stats: {
    totalNodes: number;
    completedNodes: number;
    inProgressNodes: number;
    availableNodes: number;
    lockedNodes: number;
    completionPercentage: number;
    totalExamAttempts: number;
    passedExams: number;
    averageScore: number;
    keyNodesCompleted: number;
    totalKeyNodes: number;
  };
  recentProgress: {
    nodeTitle: string;
    completedAt: string;
  }[];
}

interface OverallStats {
  totalCoursesEnrolled: number;
  totalNodesCompleted: number;
  totalExamsPassed: number;
  totalExamAttempts: number;
  averageScore: number;
}

interface RecentActivity {
  type: string;
  nodeTitle: string;
  score: number;
  passed: boolean;
  timestamp: string;
}

interface DashboardData {
  courseStats: CourseStats[];
  overallStats: OverallStats;
  recentActivity: RecentActivity[];
}

export default function LearnerDashboard() {
  const navigate = useNavigate();
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (!session) {
        navigate("/auth?redirect=/learn");
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      if (!session) {
        navigate("/auth?redirect=/learn");
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  useEffect(() => {
    if (session) {
      fetchDashboardData();
    }
  }, [session]);

  const fetchDashboardData = async () => {
    try {
      const { data, error } = await supabase.functions.invoke("get-learner-dashboard");
      if (error) throw error;
      setDashboardData(data);
    } catch (error: any) {
      console.error("Error fetching dashboard:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  if (!session) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-background/88 backdrop-blur-md sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between gap-3 min-w-0">
          <div className="flex items-center gap-3 min-w-0">
            <GraduationCap className="h-7 w-7 sm:h-8 sm:w-8 text-primary shrink-0" />
            <h1 className="text-xl sm:text-2xl font-heading font-semibold truncate">My Learning</h1>
          </div>
          <Button variant="outline" size="sm" asChild className="shrink-0">
            <Link
              to="/profile"
              className="flex items-center gap-1.5"
            >
              <Award className="h-4 w-4" />
              <span>My Profile</span>
            </Link>
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        {loading ? (
          <DashboardSkeleton />
        ) : dashboardData ? (
          <div className="space-y-8">
            {/* Overall Stats */}
            <section>
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-primary" />
                Overall Progress
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard
                  icon={<BookOpen className="h-5 w-5" />}
                  label="Courses Enrolled"
                  value={dashboardData.overallStats.totalCoursesEnrolled}
                  color="text-blue-500"
                />
                <StatCard
                  icon={<CheckCircle2 className="h-5 w-5" />}
                  label="Skills Completed"
                  value={dashboardData.overallStats.totalNodesCompleted}
                  color="text-green-500"
                />
                <StatCard
                  icon={<Trophy className="h-5 w-5" />}
                  label="Exams Passed"
                  value={dashboardData.overallStats.totalExamsPassed}
                  color="text-yellow-500"
                />
                <StatCard
                  icon={<Target className="h-5 w-5" />}
                  label="Average Score"
                  value={`${dashboardData.overallStats.averageScore}%`}
                  color="text-purple-500"
                />
              </div>
            </section>

            {/* My Courses */}
            <section>
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <BookOpen className="h-5 w-5 text-primary" />
                My Courses
              </h2>
              {dashboardData.courseStats.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <BookOpen className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground mb-4">You haven't been added to any courses yet.</p>
                    <p className="text-sm text-muted-foreground">Check your email for an invite link, or ask your instructor to add you.</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {dashboardData.courseStats.map((course) => (
                    <CourseProgressCard
                      key={course.courseId}
                      course={course}
                      onContinue={() => navigate(`/learn/courses/${course.courseId}`)}
                    />
                  ))}
                </div>
              )}
            </section>

            {/* Recent Activity */}
            {dashboardData.recentActivity.length > 0 && (
              <section>
                <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                  <Clock className="h-5 w-5 text-primary" />
                  Recent Activity
                </h2>
                <Card>
                  <CardContent className="pt-6">
                    <div className="space-y-4">
                      {dashboardData.recentActivity.map((activity, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between p-3 rounded-lg bg-muted/50"
                        >
                          <div className="flex items-center gap-3">
                            {activity.passed ? (
                              <CheckCircle2 className="h-5 w-5 text-green-500" />
                            ) : (
                              <Target className="h-5 w-5 text-orange-500" />
                            )}
                            <div>
                              <p className="font-medium">{activity.nodeTitle}</p>
                              <p className="text-sm text-muted-foreground">
                                {new Date(activity.timestamp).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          <Badge variant={activity.passed ? "default" : "secondary"}>
                            {activity.score}%
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </section>
            )}
          </div>
        ) : (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground">Failed to load dashboard data.</p>
              <Button onClick={fetchDashboardData} className="mt-4">
                Retry
              </Button>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}

function StatCard({ 
  icon, 
  label, 
  value, 
  color 
}: { 
  icon: React.ReactNode; 
  label: string; 
  value: string | number; 
  color: string;
}) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center gap-4">
          <div className={`p-3 rounded-full bg-muted ${color}`}>
            {icon}
          </div>
          <div>
            <p className="text-2xl font-bold">{value}</p>
            <p className="text-sm text-muted-foreground">{label}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

const LIME = "hsl(65,65%,75%)";

function CourseProgressCard({ 
  course, 
  onContinue 
}: { 
  course: CourseStats; 
  onContinue: () => void;
}) {
  const { stats, objectives, objectiveMastery, recentProgress } = course;
  const useMastery = objectiveMastery && objectiveMastery.length > 0;

  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-lg">{course.courseTitle}</CardTitle>
            <CardDescription className="mt-1 line-clamp-2">
              {course.courseDescription || "No description"}
            </CardDescription>
          </div>
          <Badge variant="outline" className="shrink-0">
            {stats.completionPercentage}%
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Progress Bar */}
        <div>
          <div className="flex justify-between text-sm mb-1">
            <span className="text-muted-foreground">Progress</span>
            <span className="font-medium">{stats.completedNodes}/{stats.totalNodes} skills</span>
          </div>
          <Progress value={stats.completionPercentage} className="h-2" />
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-3 gap-4 py-2">
          <div className="text-center">
            <p className="text-lg font-bold text-green-600">{stats.passedExams}</p>
            <p className="text-xs text-muted-foreground">Exams Passed</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-bold text-blue-600">{stats.averageScore}%</p>
            <p className="text-xs text-muted-foreground">Avg Score</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-bold text-purple-600">{stats.keyNodesCompleted}/{stats.totalKeyNodes}</p>
            <p className="text-xs text-muted-foreground">Key Skills</p>
          </div>
        </div>

        {/* Objectives: use learner_objective_mastery when available, else legacy objectives */}
        {(useMastery ? objectiveMastery : objectives) && (useMastery ? objectiveMastery! : objectives!).length > 0 && (
          <div>
            <p className="text-sm font-medium mb-2">Learning Objectives</p>
            <div className="space-y-2">
              {(useMastery ? objectiveMastery! : objectives!).slice(0, 5).map((obj: any, index: number) => {
                const title = useMastery ? obj.title : (typeof obj === "string" ? obj : obj?.text || obj?.title || "");
                const pct = useMastery ? Math.round((obj.masteryScore ?? 0) * 100) : (stats.completionPercentage >= ((index + 1) / objectives.length) * 100 ? 100 : 0);
                const isCompleted = pct >= 100;
                return (
                  <div key={useMastery ? obj.objectiveId : index} className="flex flex-col gap-1">
                    <div className="flex items-start gap-2 text-sm">
                      {isCompleted ? (
                        <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                      ) : (
                        <div className="h-4 w-4 rounded-full border-2 border-muted-foreground/30 mt-0.5 shrink-0" />
                      )}
                      <span className={isCompleted ? "text-muted-foreground line-through" : ""}>
                        {title}
                      </span>
                    </div>
                    {useMastery && pct > 0 && (
                      <div className="pl-6">
                        <div className="w-full bg-muted rounded-full h-1.5">
                          <div
                            className="h-1.5 rounded-full transition-all"
                            style={{ width: `${pct}%`, background: LIME }}
                          />
                        </div>
                        <span className="text-xs text-muted-foreground">{pct}% mastery</span>
                      </div>
                    )}
                  </div>
                );
              })}
              {(useMastery ? objectiveMastery! : objectives!).length > 5 && (
                <p className="text-xs text-muted-foreground pl-6">
                  +{(useMastery ? objectiveMastery! : objectives!).length - 5} more objectives
                </p>
              )}
            </div>
          </div>
        )}

        {/* Recent Progress */}
        {recentProgress.length > 0 && (
          <div className="pt-2 border-t">
            <p className="text-xs text-muted-foreground mb-2">Recently completed</p>
            <div className="flex flex-wrap gap-1">
              {recentProgress.map((item, index) => (
                <Badge key={index} variant="secondary" className="text-xs">
                  {item.nodeTitle}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Open course → skill tree */}
        <Button onClick={onContinue} className="w-full mt-2">
          {stats.completionPercentage > 0 ? "Continue Learning" : "Open course"}
          <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </CardContent>
    </Card>
  );
}

function DashboardSkeleton() {
  return (
    <div className="space-y-8">
      <section>
        <Skeleton className="h-8 w-48 mb-4" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <Skeleton className="h-12 w-12 rounded-full" />
                  <div>
                    <Skeleton className="h-6 w-16 mb-1" />
                    <Skeleton className="h-4 w-24" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>
      <section>
        <Skeleton className="h-8 w-48 mb-4" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {[...Array(2)].map((_, i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-6 w-48 mb-2" />
                <Skeleton className="h-4 w-full" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-2 w-full mb-4" />
                <div className="grid grid-cols-3 gap-4">
                  {[...Array(3)].map((_, j) => (
                    <Skeleton key={j} className="h-12 w-full" />
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>
    </div>
  );
}
